import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea, EstadoVazio } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { dataHoraBr } from '../../encomendas-fluxo';
import type { ConfigDasEncomendas } from '../../encomendas-fluxo';
import {
  MEDALHAS_SUGERIDAS,
  ROTULO_DA_CLASSIFICACAO,
  ROTULO_DO_ALERTA,
  ROTULO_DO_MOTIVO,
  diaBr,
  invalidarFalta,
  lerCampanhas,
  lerColaboradoresComFalta,
  lerDetalheDaFalta,
  lerPainelDasFaltas,
  salvarCampanha,
} from '../../faltas';
import type {
  CampanhaDeFaltas,
  ClassificacaoDaFalta,
  DetalheDaFalta,
  FiltroDoPainel,
  Medalha,
  PainelDasFaltas,
  ProdutoEmFalta,
  RegrasDasFaltas,
  VisibilidadeDoRanking,
} from '../../faltas';
import { FotoDoProduto } from '../auditoria/FotoDoProduto';
import { Gaveta } from './Gaveta';
import { GerarCotac } from './GerarCotac';
import './faltas.css';

/**
 * ENCOMENDAS → FALTAS REGISTRADAS (dono, 02/10/2026) — a inteligência que o clique do balconista
 * alimenta: o que os clientes procuram e não estamos conseguindo vender.
 *
 * KPIs · demanda não atendida · alertas · ranking de produtos · divergências HOS × físico ·
 * participação por colaborador (métrica de participação, NUNCA dinheiro). Clicar num produto abre
 * a gaveta com o histórico. Só para `encomendas.ver_faltas` — o servidor confere cada rota.
 */

type Periodo = 'hoje' | 'ontem' | '7' | '30' | 'custom';
type Visao = 'produtos' | 'divergencias' | 'colaboradores';

function intervalo(p: Periodo, de: string, ate: string): { de: string; ate: string } {
  switch (p) {
    case 'hoje':
      return { de: diaBr(), ate: diaBr() };
    case 'ontem':
      return { de: diaBr(-1), ate: diaBr(-1) };
    case '7':
      return { de: diaBr(-6), ate: diaBr() };
    case '30':
      return { de: diaBr(-29), ate: diaBr() };
    default:
      return { de, ate };
  }
}

const ROTULO_DO_PERIODO: Readonly<Record<Periodo, string>> = {
  hoje: 'hoje',
  ontem: 'ontem',
  '7': 'nos últimos 7 dias',
  '30': 'nos últimos 30 dias',
  custom: 'no período',
};

export function FaltasRegistradas({ sessao }: { sessao: Sessao }) {
  const [periodo, setPeriodo] = useState<Periodo>('7');
  const [de, setDe] = useState(diaBr(-6));
  const [ate, setAte] = useState(diaBr());
  const [busca, setBusca] = useState('');
  const [fabricante, setFabricante] = useState('');
  const [conta, setConta] = useState('');
  const [situacao, setSituacao] = useState<'' | 'RUPTURA' | 'DIVERGENCIA'>('');
  const [origem, setOrigem] = useState<
    '' | 'ENCOMENDAS_CONSULTA' | 'MIA_WHATSAPP' | 'EQUIPE_WHATSAPP'
  >('');
  const [minimo, setMinimo] = useState(1);
  const [visao, setVisao] = useState<Visao>('produtos');
  const [aberto, setAberto] = useState<string | null>(null);
  const [gerandoCotac, setGerandoCotac] = useState(false);
  const [colaboradores, setColaboradores] = useState<
    readonly { accountId: string; nome: string }[]
  >([]);
  const [estado, setEstado] = useState<
    | { fase: 'carregando' }
    | { fase: 'erro' }
    | { fase: 'pronto'; painel: PainelDasFaltas; regras: RegrasDasFaltas }
  >({ fase: 'carregando' });

  useEffect(() => {
    void lerColaboradoresComFalta().then((r) => r.ok && setColaboradores(r.dados.colaboradores));
  }, []);

  // Busca e fabricante esperam o dedo parar (não dispara uma consulta por letra).
  const [buscaFirme, setBuscaFirme] = useState('');
  const [fabricanteFirme, setFabricanteFirme] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => {
      setBuscaFirme(busca);
      setFabricanteFirme(fabricante);
    }, 300);
    return () => window.clearTimeout(t);
  }, [busca, fabricante]);

  const filtro: FiltroDoPainel = useMemo(
    () => ({
      ...intervalo(periodo, de, ate),
      q: buscaFirme,
      fabricante: fabricanteFirme,
      ...(conta ? { conta } : {}),
      ...(situacao ? { situacao } : {}),
      ...(origem ? { origem } : {}),
      minimo,
    }),
    [periodo, de, ate, buscaFirme, fabricanteFirme, conta, situacao, origem, minimo],
  );

  const carregar = useCallback(() => {
    if (filtro.ate < filtro.de) return;
    let vivo = true;
    void lerPainelDasFaltas(filtro).then((r) => {
      if (!vivo) return;
      setEstado(
        r.ok
          ? { fase: 'pronto', painel: r.dados.painel, regras: r.dados.regras }
          : { fase: 'erro' },
      );
    });
    return () => {
      vivo = false;
    };
  }, [filtro]);
  useEffect(() => {
    const parar = carregar();
    const t = window.setInterval(() => carregar(), 30_000);
    return () => {
      parar?.();
      window.clearInterval(t);
    };
  }, [carregar]);

  const p = estado.fase === 'pronto' ? estado.painel : null;
  const noPeriodo = ROTULO_DO_PERIODO[periodo];

  return (
    <div className="ft">
      <section className="ft-filtros" aria-label="Filtros das faltas">
        <div className="enc-filtros" role="group" aria-label="Período">
          {(
            [
              ['hoje', 'Hoje'],
              ['ontem', 'Ontem'],
              ['7', '7 dias'],
              ['30', '30 dias'],
              ['custom', 'Personalizado'],
            ] as const
          ).map(([k, rotulo]) => (
            <button
              key={k}
              type="button"
              className={periodo === k ? 'enc-filtro-sel' : ''}
              aria-pressed={periodo === k}
              onClick={() => setPeriodo(k)}
            >
              {rotulo}
            </button>
          ))}
        </div>
        {periodo === 'custom' && (
          <div className="ft-datas">
            <label>
              De
              <input type="date" value={de} max={ate} onChange={(e) => setDe(e.target.value)} />
            </label>
            <label>
              até
              <input type="date" value={ate} min={de} onChange={(e) => setAte(e.target.value)} />
            </label>
          </div>
        )}
        <div className="ft-busca">
          <Icone nome="search" tamanho={17} traco={1.7} />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar produto, EAN ou código HOS..."
            aria-label="Buscar produto, EAN ou código HOS"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="ft-mais">
          <input
            value={fabricante}
            onChange={(e) => setFabricante(e.target.value)}
            placeholder="Fabricante"
            aria-label="Fabricante"
          />
          <select value={conta} onChange={(e) => setConta(e.target.value)} aria-label="Balconista">
            <option value="">Todos os colaboradores</option>
            {colaboradores.map((c) => (
              <option key={c.accountId} value={c.accountId}>
                {c.nome}
              </option>
            ))}
          </select>
          <select
            value={situacao}
            onChange={(e) => setSituacao(e.target.value as typeof situacao)}
            aria-label="Situação"
          >
            <option value="">Sem estoque e divergência</option>
            <option value="RUPTURA">Só sem estoque</option>
            <option value="DIVERGENCIA">Só divergência</option>
          </select>
          <select
            value={origem}
            onChange={(e) => setOrigem(e.target.value as typeof origem)}
            aria-label="Origem"
          >
            <option value="">Todas as origens</option>
            <option value="ENCOMENDAS_CONSULTA">Balcão</option>
            <option value="MIA_WHATSAPP">MIA (WhatsApp)</option>
            <option value="EQUIPE_WHATSAPP">Equipe do WhatsApp</option>
          </select>
          <label className="ft-minimo">
            Mín. de faltas
            <input
              inputMode="numeric"
              value={String(minimo)}
              onChange={(e) =>
                setMinimo(
                  Math.max(1, Math.min(9_999, Number(e.target.value.replace(/\D/g, '')) || 1)),
                )
              }
              aria-label="Quantidade mínima de faltas"
            />
          </label>
        </div>
      </section>

      {estado.fase === 'erro' ? (
        <ErroDeArea oQue="as faltas registradas" aoTentar={() => carregar()} />
      ) : (
        <>
          <div className={`enc-kpis${p ? '' : ' enc-esqueleto'}`} aria-busy={!p}>
            {(
              [
                ['Faltas hoje', p?.indicadores.hoje],
                ['Últimos 7 dias', p?.indicadores.ultimos7],
                ...(periodo === '7' || periodo === 'hoje'
                  ? []
                  : ([[`Faltas ${noPeriodo}`, p?.indicadores.noPeriodo]] as const)),
                ['Produtos diferentes', p?.indicadores.produtosDiferentes],
                ['Possíveis divergências', p?.indicadores.produtosComDivergencia],
                ['Viraram encomenda (até 2 h)', p?.indicadores.viraramEncomenda],
              ] as const
            ).map(([rotulo, v]) => (
              <div
                key={rotulo}
                className={`enc-kpi${rotulo === 'Possíveis divergências' && (v ?? 0) > 0 ? ' ft-kpi-divergencia' : ''}`}
              >
                <span>{rotulo}</span>
                {v === undefined ? <i /> : <b>{String(v)}</b>}
              </div>
            ))}
          </div>

          {p && p.demandaNaoAtendida.length > 0 && (
            <section className="ft-demanda" aria-label="Demanda não atendida">
              <h2>
                <Icone nome="warn" tamanho={16} traco={1.8} /> Demanda não atendida
              </h2>
              <ul>
                {p.demandaNaoAtendida.map((d) => (
                  <li key={d.productId}>
                    <button type="button" onClick={() => setAberto(d.productId)}>
                      <b>{d.descricao}</b> foi procurado {String(d.vezes)}{' '}
                      {d.vezes === 1 ? 'vez' : 'vezes'} nos últimos 7 dias sem disponibilidade.
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {p && p.alertas.length > 0 && (
            <section className="enc-atencao" aria-label="Alertas de falta">
              <h2>Produtos com aumento de procura e indisponibilidade (24 h)</h2>
              <ul>
                {p.alertas.slice(0, 12).map((a) => (
                  <li key={a.productId}>
                    <button
                      type="button"
                      className={`enc-atencao-item ft-alerta-${(a.alerta ?? 'ATENCAO').toLowerCase()}`}
                      onClick={() => setAberto(a.productId)}
                    >
                      <span className="ft-alerta-texto">
                        <span className="ft-alerta-nivel">
                          {a.alerta === 'CRITICO' ? '🔥 ' : ''}
                          {ROTULO_DO_ALERTA[a.alerta ?? 'ATENCAO']}
                        </span>
                        {a.descricao} · {String(a.faltas24h)} em 24 h
                      </span>
                      <Icone nome="arrow" tamanho={14} traco={1.8} />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="ft-visoes-linha">
            <nav className="enc-filtros ft-visoes" aria-label="Visão">
              {(
                [
                  ['produtos', 'Mais procurados em falta'],
                  [
                    'divergencias',
                    `Divergências de estoque${p ? ` · ${String(p.divergencias.length)}` : ''}`,
                  ],
                  ['colaboradores', 'Colaboradores'],
                ] as const
              ).map(([k, rotulo]) => (
                <button
                  key={k}
                  type="button"
                  className={visao === k ? 'enc-filtro-sel' : ''}
                  aria-pressed={visao === k}
                  onClick={() => setVisao(k)}
                >
                  {rotulo}
                </button>
              ))}
            </nav>
            <button
              type="button"
              className="enc-botao-sec"
              disabled={filtro.ate < filtro.de}
              onClick={() => setGerandoCotac(true)}
            >
              Gerar TXT p/ SmartPed
            </button>
          </div>

          {!p ? (
            <div className="enc-lista-esq enc-esqueleto">
              <i />
              <i />
              <i />
            </div>
          ) : visao === 'produtos' ? (
            <TabelaDeProdutos produtos={p.produtos} aoAbrir={setAberto} />
          ) : visao === 'divergencias' ? (
            <TabelaDeDivergencias produtos={p.divergencias} aoAbrir={setAberto} />
          ) : (
            <TabelaDeColaboradores colaboradores={p.colaboradores} />
          )}
        </>
      )}

      {gerandoCotac && (
        <GerarCotac sessao={sessao} filtro={filtro} aoFechar={() => setGerandoCotac(false)} />
      )}

      {aberto && (
        <GavetaDaFalta
          sessao={sessao}
          productId={aberto}
          aoFechar={() => setAberto(null)}
          aoMudar={() => carregar()}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function SeloDaFalta({ c }: { c: ClassificacaoDaFalta }) {
  return (
    <span className={`ft-selo ft-selo-${c.toLowerCase()}`}>
      {c === 'DIVERGENCIA' ? '⚠ ' : ''}
      {ROTULO_DA_CLASSIFICACAO[c]}
    </span>
  );
}

const quando = (iso: string) => {
  const d = new Date(iso);
  const hoje = diaBr();
  const dia = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(d);
  const hora = d.toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  });
  if (dia === hoje) return `Hoje ${hora}`;
  if (dia === diaBr(-1)) return `Ontem ${hora}`;
  return dataHoraBr(iso);
};

const estoqueTexto = (n: number | null) =>
  n === null ? '—' : n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });

function CelulaDoProduto({ p }: { p: ProdutoEmFalta }) {
  return (
    <span className="ft-produto">
      <FotoDoProduto url={p.foto} nome={p.descricao} tamanho={36} />
      <span>
        <b>{p.descricao}</b>
        <small>{p.fabricante ?? 'Fabricante não informado'}</small>
      </span>
    </span>
  );
}

function TabelaDeProdutos({
  produtos,
  aoAbrir,
}: {
  produtos: readonly ProdutoEmFalta[];
  aoAbrir: (id: string) => void;
}) {
  if (produtos.length === 0)
    return (
      <EstadoVazio
        icone="check"
        titulo="Nenhuma falta registrada com esses filtros"
        texto="Quando o balconista registrar uma falta, ela aparece aqui na hora."
      />
    );
  return (
    <div className="enc-col ft-tabela-caixa">
      <table className="cv-tabela ft-tabela">
        <thead>
          <tr>
            <th>Produto</th>
            <th>Código HOS</th>
            <th className="cv-dir">Estoque atual</th>
            <th className="cv-dir">Faltas</th>
            <th>Última falta</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {produtos.map((p) => (
            <tr
              key={p.productId}
              className="cv-clicavel"
              tabIndex={0}
              onClick={() => aoAbrir(p.productId)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && aoAbrir(p.productId)}
            >
              <td>
                <CelulaDoProduto p={p} />
              </td>
              <td className="enc-mono">{p.codigoHos ?? '—'}</td>
              <td className="cv-dir enc-mono">{estoqueTexto(p.estoqueAtual)}</td>
              <td className="cv-dir">
                <b className="ft-n">{String(p.faltas)}</b>
                {p.alerta && (
                  <span className={`ft-alerta-mini ft-alerta-${p.alerta.toLowerCase()}`}>
                    {p.alerta === 'CRITICO' ? '🔥' : '▲'} {String(p.faltas24h)}/24 h
                  </span>
                )}
              </td>
              <td>{quando(p.ultimaEm)}</td>
              <td>
                <SeloDaFalta c={p.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TabelaDeDivergencias({
  produtos,
  aoAbrir,
}: {
  produtos: readonly ProdutoEmFalta[];
  aoAbrir: (id: string) => void;
}) {
  if (produtos.length === 0)
    return (
      <EstadoVazio
        icone="check"
        titulo="Nenhuma divergência de estoque no período"
        texto="Divergência = o HOS diz que tem, mas o balconista registrou falta."
      />
    );
  return (
    <div className="enc-col ft-tabela-caixa">
      <p className="ft-explica">
        O HOS diz que existe estoque, mas o balconista não achou o produto. Pode ser estoque
        incorreto, perda, produto fora do lugar ou erro de entrada — o sistema não adivinha o
        motivo: <b>confira o estoque físico</b>.
      </p>
      <table className="cv-tabela ft-tabela">
        <thead>
          <tr>
            <th>Produto</th>
            <th>Código HOS</th>
            <th className="cv-dir">HOS agora</th>
            <th className="cv-dir">HOS na última falta</th>
            <th className="cv-dir">Faltas</th>
            <th className="cv-dir">Hoje</th>
            <th>Ação</th>
          </tr>
        </thead>
        <tbody>
          {produtos.map((p) => (
            <tr
              key={p.productId}
              className="cv-clicavel"
              tabIndex={0}
              onClick={() => aoAbrir(p.productId)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && aoAbrir(p.productId)}
            >
              <td>
                <CelulaDoProduto p={p} />
              </td>
              <td className="enc-mono">{p.codigoHos ?? '—'}</td>
              <td className="cv-dir enc-mono">{estoqueTexto(p.estoqueAtual)}</td>
              <td className="cv-dir enc-mono">{estoqueTexto(p.estoqueNoUltimoRegistro)}</td>
              <td className="cv-dir">
                <b className="ft-n">{String(p.faltas)}</b>
              </td>
              <td className="cv-dir">{String(p.faltasHoje)}</td>
              <td>
                <span className="ft-selo ft-selo-divergencia">⚠ Conferir estoque físico</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TabelaDeColaboradores({
  colaboradores,
}: {
  colaboradores: PainelDasFaltas['colaboradores'];
}) {
  if (colaboradores.length === 0)
    return <EstadoVazio icone="users" titulo="Ninguém registrou falta com esses filtros" />;
  return (
    <div className="enc-col ft-tabela-caixa">
      <p className="ft-explica">
        Métrica de <b>participação</b>: quem está ajudando a descobrir o que falta. Não vira
        dinheiro nem comissão. “Válidas” = as que contam para campanha (sem repetição em pouco
        tempo).
      </p>
      <table className="cv-tabela ft-tabela">
        <thead>
          <tr>
            <th>Colaborador</th>
            <th className="cv-dir">Registros</th>
            <th className="cv-dir">Produtos</th>
            <th className="cv-dir">Divergências</th>
            <th className="cv-dir">Válidas</th>
          </tr>
        </thead>
        <tbody>
          {colaboradores.map((c) => (
            <tr key={c.accountId}>
              <td>
                <b>{c.nome}</b>
              </td>
              <td className="cv-dir enc-mono">{String(c.registros)}</td>
              <td className="cv-dir enc-mono">{String(c.produtos)}</td>
              <td className="cv-dir enc-mono">{String(c.divergencias)}</td>
              <td className="cv-dir enc-mono">{String(c.validasCampanha)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Gaveta do produto
 * ------------------------------------------------------------------ */

function GavetaDaFalta({
  sessao,
  productId,
  aoFechar,
  aoMudar,
}: {
  sessao: Sessao;
  productId: string;
  aoFechar: () => void;
  aoMudar: () => void;
}) {
  const [d, setD] = useState<DetalheDaFalta | null>(null);
  const [erro, setErro] = useState(false);
  const [invalidando, setInvalidando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const ler = useCallback(() => {
    setErro(false);
    void lerDetalheDaFalta(productId).then((r) => (r.ok ? setD(r.dados.detalhe) : setErro(true)));
  }, [productId]);
  useEffect(ler, [ler]);

  const invalidar = async (id: string) => {
    const r = await invalidarFalta(sessao, id, motivo.trim());
    if (r.ok) {
      setInvalidando(null);
      setMotivo('');
      setMsg('Falta invalidada. Ela continua no histórico, com o motivo.');
      ler();
      aoMudar();
    } else
      setMsg(
        r.falha.tipo === 'invalido' ? r.falha.mensagem : 'Não foi possível invalidar a falta.',
      );
  };

  const fechar = useCallback(() => aoFechar(), [aoFechar]);
  return (
    <Gaveta titulo={d?.produto.descricao ?? 'Falta registrada'} aoFechar={fechar} largura={620}>
      {erro ? (
        <ErroDeArea oQue="o histórico do produto" aoTentar={ler} />
      ) : !d ? (
        <div className="enc-lista-esq enc-esqueleto">
          <i />
          <i />
        </div>
      ) : (
        <div className="ft-gaveta">
          <section className="ft-gaveta-produto">
            <FotoDoProduto url={d.produto.foto} nome={d.produto.descricao} tamanho={84} />
            <div>
              <b>{d.produto.descricao}</b>
              <p className="enc-cartao-sub">
                {[d.produto.fabricante, d.produto.principioAtivo].filter(Boolean).join(' · ') ||
                  'Fabricante não informado'}
              </p>
              <p className="enc-codigos">
                {d.produto.ean && <span>EAN {d.produto.ean}</span>}
                {d.produto.codigoHos && <span>Cód. HOS {d.produto.codigoHos}</span>}
              </p>
            </div>
          </section>

          <dl className="ft-gaveta-numeros">
            <div>
              <dt>Estoque atual (HOS)</dt>
              <dd>{estoqueTexto(d.produto.estoqueAtual)}</dd>
            </div>
            <div>
              <dt>Total de faltas</dt>
              <dd>{String(d.total)}</dd>
            </div>
            <div>
              <dt>Com estoque no HOS</dt>
              <dd className={d.divergencias > 0 ? 'enc-n-zero' : ''}>{String(d.divergencias)}</dd>
            </div>
            <div>
              <dt>Últimos 7 · 30 dias</dt>
              <dd>
                {String(d.ultimos7)} · {String(d.ultimos30)}
              </dd>
            </div>
            <div>
              <dt>Frequência</dt>
              <dd>
                {d.porDia === null
                  ? '—'
                  : `${d.porDia.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}/dia`}
              </dd>
            </div>
            <div>
              <dt>Primeira · última</dt>
              <dd className="ft-gaveta-datas">
                {dataHoraBr(d.primeiraEm)} · {dataHoraBr(d.ultimaEm)}
              </dd>
            </div>
          </dl>

          {msg && <p className="enc-faixa-ok">{msg}</p>}

          <h3 className="ft-gaveta-titulo">Histórico</h3>
          {d.ocorrencias.length === 0 ? (
            <p className="enc-dica">Nenhuma falta registrada para este produto.</p>
          ) : (
            <ul className="ft-historico">
              {d.ocorrencias.map((o) => (
                <li key={o.id} className={o.invalidada ? 'ft-invalidada' : ''}>
                  <div className="ft-historico-linha">
                    <span className="enc-mono">{dataHoraBr(o.em)}</span>
                    <b>{o.nome}</b>
                    <span>
                      Estoque HOS: {estoqueTexto(o.estoqueHos)}
                      {o.estoqueFonte === 'ESPELHO' ? ' (espelho)' : ''}
                    </span>
                    <SeloDaFalta c={o.classificacao} />
                  </div>
                  <div className="ft-historico-extra">
                    {o.termoBusca && <span>buscou “{o.termoBusca}”</span>}
                    {!o.elegivelCampanha && o.motivoInelegivel && (
                      <span>
                        não conta p/ campanha:{' '}
                        {ROTULO_DO_MOTIVO[o.motivoInelegivel] ?? o.motivoInelegivel}
                      </span>
                    )}
                    {o.invalidada ? (
                      <span className="ft-invalidada-motivo">
                        Invalidada por {o.invalidada.por} em {dataHoraBr(o.invalidada.em)} —{' '}
                        {o.invalidada.motivo}
                      </span>
                    ) : invalidando === o.id ? null : (
                      <button
                        type="button"
                        className="enc-link"
                        onClick={() => {
                          setInvalidando(o.id);
                          setMotivo('');
                          setMsg(null);
                        }}
                      >
                        Invalidar
                      </button>
                    )}
                  </div>
                  {invalidando === o.id && (
                    <div className="enc-motivo ft-invalidar">
                      <textarea
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        placeholder="Por que esta falta não vale? (fica na trilha de auditoria)"
                        rows={2}
                        maxLength={300}
                        aria-label="Motivo da invalidação"
                      />
                      <div>
                        <button
                          type="button"
                          className="enc-botao-sec"
                          onClick={() => setInvalidando(null)}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          className="enc-botao"
                          disabled={motivo.trim().length < 5}
                          onClick={() => void invalidar(o.id)}
                        >
                          Invalidar falta
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Gaveta>
  );
}

/* ------------------------------------------------------------------ *
 * Configurações → antiabuso, alertas e campanhas
 * ------------------------------------------------------------------ */

const ROTULO_DA_VISIBILIDADE: Readonly<Record<VisibilidadeDoRanking, string>> = {
  PUBLICO: 'Ranking público para os colaboradores',
  PROPRIA_POSICAO: 'Cada um vê só a própria posição',
  SO_ADMIN: 'Ranking só para o admin',
};

export function ConfiguracoesDasFaltas({
  sessao,
  cfg,
  salvar,
}: {
  sessao: Sessao;
  cfg: ConfigDasEncomendas;
  salvar: (chave: keyof ConfigDasEncomendas, valor: unknown) => Promise<void> | void;
}) {
  const f = cfg.faltas;
  const campo = (k: keyof RegrasDasFaltas, rotulo: string) => (
    <LinhaDeRegra
      key={k}
      rotulo={rotulo}
      valor={f[k]}
      aoSalvar={(v) => void salvar('faltas', { ...f, [k]: v })}
    />
  );
  return (
    <>
      <section className="enc-col">
        <header className="enc-col-cabeca">
          <h2>Registrar falta · antiabuso e alertas</h2>
        </header>
        <div className="enc-col-corpo enc-config-linhas">
          <p className="enc-dica">
            Toda falta é gravada. Estas regras só decidem o que é <b>clique repetido</b> (vira a
            mesma ocorrência) e o que <b>vale para campanha</b>.
          </p>
          {campo('repeticaoSegundos', 'Clique repetido do mesmo produto dentro de (segundos)')}
          {campo('intervaloValidoMin', 'Nova procura vale p/ campanha depois de (min)')}
          {campo('maxValidasPorProdutoDia', 'Máximo de válidas por produto no dia (por pessoa)')}
          {campo('maxValidasPorDia', 'Máximo de válidas no dia (por pessoa)')}
          {campo('alertaAtencao', 'Alerta “Atenção” com faltas em 24 h')}
          {campo('alertaPrioridade', 'Alerta “Prioridade” com faltas em 24 h')}
          {campo('alertaCritico', 'Alerta “🔥 Alta demanda não atendida” com faltas em 24 h')}
        </div>
      </section>
      <CampanhasDeFaltas sessao={sessao} />
    </>
  );
}

function LinhaDeRegra({
  rotulo,
  valor,
  aoSalvar,
}: {
  rotulo: string;
  valor: number;
  aoSalvar: (v: number) => void;
}) {
  const [v, setV] = useState(String(valor));
  useEffect(() => setV(String(valor)), [valor]);
  return (
    <div className="enc-config-linha">
      <span>{rotulo}</span>
      <input
        value={v}
        onChange={(e) => setV(e.target.value.replace(/\D/g, ''))}
        inputMode="numeric"
        aria-label={rotulo}
      />
      <button
        type="button"
        className="enc-botao-sec"
        disabled={v === String(valor) || v === ''}
        onClick={() => aoSalvar(Number(v))}
      >
        Salvar
      </button>
    </div>
  );
}

interface Rascunho {
  id: string | null;
  nome: string;
  descricao: string;
  inicio: string;
  fim: string;
  ativa: boolean;
  medalhas: Medalha[];
  rankingVisibilidade: VisibilidadeDoRanking;
}

const fimDoMes = () => {
  const [a, m] = diaBr().split('-').map(Number) as [number, number];
  const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate();
  return `${String(a)}-${String(m).padStart(2, '0')}-${String(ultimo).padStart(2, '0')}`;
};

function CampanhasDeFaltas({ sessao }: { sessao: Sessao }) {
  const [lista, setLista] = useState<readonly CampanhaDeFaltas[] | null>(null);
  const [ranking, setRanking] = useState<
    readonly { accountId: string; nome: string; validas: number; posicao: number }[]
  >([]);
  const [rasc, setRasc] = useState<Rascunho | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const ler = useCallback(() => {
    void lerCampanhas().then((r) => {
      if (!r.ok) return;
      setLista(r.dados.campanhas);
      setRanking(r.dados.ranking);
    });
  }, []);
  useEffect(ler, [ler]);

  const editar = (c: CampanhaDeFaltas | null) => {
    setMsg(null);
    setRasc(
      c
        ? {
            id: c.id,
            nome: c.nome,
            descricao: c.descricao ?? '',
            inicio: c.inicio,
            fim: c.fim,
            ativa: c.ativa,
            medalhas: [...c.medalhas],
            rankingVisibilidade: c.rankingVisibilidade,
          }
        : {
            id: null,
            nome: 'Caçador de Faltas',
            descricao: 'Registre as faltas que o cliente procurou e não achou.',
            inicio: `${diaBr().slice(0, 8)}01`,
            fim: fimDoMes(),
            ativa: false,
            medalhas: MEDALHAS_SUGERIDAS.map((m) => ({ ...m })),
            rankingVisibilidade: 'PROPRIA_POSICAO',
          },
    );
  };

  const gravar = async (r: Rascunho) => {
    const res = await salvarCampanha(sessao, {
      ...r,
      descricao: r.descricao.trim() || null,
    });
    if (res.ok) {
      setRasc(null);
      setMsg('Campanha salva (fica na trilha de auditoria).');
      ler();
    } else setMsg(res.falha.tipo === 'invalido' ? res.falha.mensagem : 'Não foi possível salvar.');
  };

  const ligar = (c: CampanhaDeFaltas, ativa: boolean) =>
    void gravar({
      id: c.id,
      nome: c.nome,
      descricao: c.descricao ?? '',
      inicio: c.inicio,
      fim: c.fim,
      ativa,
      medalhas: [...c.medalhas],
      rankingVisibilidade: c.rankingVisibilidade,
    });

  const br = (d: string) => d.split('-').reverse().join('/');
  return (
    <section className="enc-col">
      <header className="enc-col-cabeca">
        <h2>Campanhas de registro de falta</h2>
        <button type="button" className="enc-botao-sec" onClick={() => editar(null)}>
          <Icone nome="plus" tamanho={14} traco={1.8} /> Nova campanha
        </button>
      </header>
      <div className="enc-col-corpo">
        <p className="enc-dica">
          Estimula a equipe a registrar faltas. Conta só registro <b>válido</b> (o antiabuso acima)
          e <b>não vira dinheiro</b>. Uma campanha ligada por vez.
        </p>
        {msg && <p className="enc-faixa-ok">{msg}</p>}
        {lista === null ? (
          <div className="enc-lista-esq enc-esqueleto">
            <i />
          </div>
        ) : lista.length === 0 ? (
          <p className="enc-dica">Nenhuma campanha criada ainda.</p>
        ) : (
          <ul className="ft-campanhas">
            {lista.map((c) => (
              <li key={c.id}>
                <div>
                  <b>{c.nome}</b>
                  <span className={`ft-selo ${c.emVigor ? 'ft-selo-ok' : 'ft-selo-neutro'}`}>
                    {c.emVigor
                      ? 'Em andamento'
                      : c.ativa
                        ? 'Ligada · fora do período'
                        : 'Desligada'}
                  </span>
                  <small>
                    {br(c.inicio)} → {br(c.fim)} · {ROTULO_DA_VISIBILIDADE[c.rankingVisibilidade]}
                  </small>
                  <small>
                    {c.medalhas
                      .map((m) => `${m.emoji} ${m.nome} (${String(m.minimo)})`)
                      .join(' · ')}
                  </small>
                </div>
                <div className="ft-campanha-acoes">
                  <label className="enc-config-linha ft-ligar">
                    <span>Ligada</span>
                    <input
                      type="checkbox"
                      role="switch"
                      className="enc-interruptor"
                      checked={c.ativa}
                      onChange={(e) => ligar(c, e.target.checked)}
                    />
                  </label>
                  <button type="button" className="enc-botao-sec" onClick={() => editar(c)}>
                    Editar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {ranking.length > 0 && (
          <div>
            <h3 className="ft-gaveta-titulo">Ranking da campanha em andamento</h3>
            <ol className="ft-ranking">
              {ranking.slice(0, 10).map((x) => (
                <li key={x.accountId}>
                  <span className="ft-pos">{String(x.posicao)}º</span>
                  <b>{x.nome}</b>
                  <span className="enc-mono">{String(x.validas)} válidas</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {rasc && <EditorDeCampanha r={rasc} aoMudar={setRasc} aoSalvar={gravar} />}
      </div>
    </section>
  );
}

function EditorDeCampanha({
  r,
  aoMudar,
  aoSalvar,
}: {
  r: Rascunho;
  aoMudar: (r: Rascunho | null) => void;
  aoSalvar: (r: Rascunho) => Promise<void>;
}) {
  const mudarMedalha = (i: number, m: Partial<Medalha>) =>
    aoMudar({ ...r, medalhas: r.medalhas.map((x, j) => (j === i ? { ...x, ...m } : x)) });
  return (
    <div className="ft-editor">
      <div className="ft-editor-grade">
        <label className="enc-campo enc-campo-largo">
          <span className="enc-campo-rotulo">Nome</span>
          <input
            value={r.nome}
            maxLength={80}
            onChange={(e) => aoMudar({ ...r, nome: e.target.value })}
          />
        </label>
        <label className="enc-campo enc-campo-largo">
          <span className="enc-campo-rotulo">Descrição (opcional)</span>
          <input
            value={r.descricao}
            maxLength={300}
            onChange={(e) => aoMudar({ ...r, descricao: e.target.value })}
          />
        </label>
        <label className="enc-campo">
          <span className="enc-campo-rotulo">Início</span>
          <input
            type="date"
            value={r.inicio}
            onChange={(e) => aoMudar({ ...r, inicio: e.target.value })}
          />
        </label>
        <label className="enc-campo">
          <span className="enc-campo-rotulo">Fim</span>
          <input
            type="date"
            value={r.fim}
            onChange={(e) => aoMudar({ ...r, fim: e.target.value })}
          />
        </label>
        <label className="enc-campo enc-campo-largo">
          <span className="enc-campo-rotulo">Quem vê o ranking</span>
          <select
            value={r.rankingVisibilidade}
            onChange={(e) =>
              aoMudar({ ...r, rankingVisibilidade: e.target.value as VisibilidadeDoRanking })
            }
          >
            {(Object.keys(ROTULO_DA_VISIBILIDADE) as VisibilidadeDoRanking[]).map((k) => (
              <option key={k} value={k}>
                {ROTULO_DA_VISIBILIDADE[k]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <span className="enc-campo-rotulo">Medalhas (registros válidos para ganhar)</span>
      <ul className="ft-medalhas-editor">
        {r.medalhas.map((m, i) => (
          <li key={m.codigo}>
            <input
              className="ft-emoji"
              value={m.emoji}
              maxLength={8}
              aria-label="Emoji"
              onChange={(e) => mudarMedalha(i, { emoji: e.target.value })}
            />
            <input
              value={m.nome}
              maxLength={40}
              aria-label="Nome da medalha"
              onChange={(e) => mudarMedalha(i, { nome: e.target.value })}
            />
            <input
              className="ft-min"
              inputMode="numeric"
              value={String(m.minimo)}
              aria-label="Registros válidos"
              onChange={(e) =>
                mudarMedalha(i, { minimo: Number(e.target.value.replace(/\D/g, '')) || 0 })
              }
            />
            <button
              type="button"
              className="enc-link"
              disabled={r.medalhas.length <= 1}
              onClick={() => aoMudar({ ...r, medalhas: r.medalhas.filter((_, j) => j !== i) })}
            >
              Tirar
            </button>
          </li>
        ))}
      </ul>
      {r.medalhas.length < 8 && (
        <button
          type="button"
          className="enc-link"
          onClick={() =>
            aoMudar({
              ...r,
              medalhas: [
                ...r.medalhas,
                {
                  codigo: `m${String(Date.now()).slice(-8)}`,
                  nome: 'Nova medalha',
                  emoji: '🏅',
                  minimo: (r.medalhas.at(-1)?.minimo ?? 0) + 10,
                },
              ],
            })
          }
        >
          + Medalha
        </button>
      )}
      <label className="enc-config-linha">
        <span>Ligar a campanha ao salvar (desliga a que estiver ligada)</span>
        <input
          type="checkbox"
          role="switch"
          className="enc-interruptor"
          checked={r.ativa}
          onChange={(e) => aoMudar({ ...r, ativa: e.target.checked })}
        />
      </label>
      <div className="ft-editor-rodape">
        <button type="button" className="enc-botao-sec" onClick={() => aoMudar(null)}>
          Cancelar
        </button>
        <button type="button" className="enc-botao" onClick={() => void aoSalvar(r)}>
          Salvar campanha
        </button>
      </div>
    </div>
  );
}
